import React from 'react'
import styled, { css, DefaultTheme, ThemeProps } from 'styled-components'
import { NavLink } from 'react-router-dom'

// Components
import { ButtonUnstyled } from 'components/Button'

interface TabButtonProps {
  selected?: boolean
  borderPosition: 'top' | 'bottom'
}

const tabStyle = css`
  border: 1px solid transparent;
  color: ${({ selected, theme }) =>
    selected ? theme.colors.text.dark.base : theme.colors.text.dark.secondary};
  display: inline-block;
  font-size: 0.6875rem;
  line-height: 1.5;
  font-family: ${({ theme }) => theme.fonts.buttons};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  padding: 10px 16px; /* stylelint-disable-line scales/space */
  position: relative;
  text-align: center;
  text-transform: uppercase;
  transition: all ${({ theme }) => theme.transitions.duration[1]};
  user-select: none;
  white-space: nowrap;

  &:hover,
  &:focus,
  &:active {
    color: ${({ theme }) => theme.colors.text.dark.base};
  }

  ${({
    selected,
    borderPosition,
    theme,
  }: TabButtonProps & ThemeProps<DefaultTheme>) =>
    `${borderPosition === 'bottom' ? 'border-bottom' : 'border-top'}: ${
      selected
        ? `2px solid ${theme.colors.primaryTint[100]}`
        : '2px solid transparent'
    };`}
`

export const TabButton = styled(ButtonUnstyled)<TabButtonProps>`
  ${tabStyle};
`

export const TabDiv = styled.span<TabButtonProps>`
  cursor: pointer;

  ${tabStyle};
`

export interface TabProps {
  selected?: boolean
  borderPosition?: 'top' | 'bottom'
  onTabSelected?: () => void
  urlFragment?: string
  className?: string
  isDisabled?: boolean
}

const Tab: React.FC<TabProps> = ({
  children,
  selected = false,
  borderPosition = 'top',
  urlFragment,
  onTabSelected,
  className,
  isDisabled,
}) => (
  <li className={className}>
    {urlFragment ? (
      <NavLink to={`#${urlFragment}`}>
        <TabDiv
          selected={selected}
          borderPosition={borderPosition}
          onClick={onTabSelected}>
          {children}
        </TabDiv>
      </NavLink>
    ) : (
      <TabButton
        selected={selected}
        borderPosition={borderPosition}
        onClick={onTabSelected}
        disabled={isDisabled}>
        {children}
      </TabButton>
    )}
  </li>
)

export default Tab
